import { useEffect, useRef, useState } from "react"

export type EversignSDK = {
  open: (config: {
    url: string
    containerID: string
    width: number
    height: number
    events: {
      loaded?: () => void
      signed?: () => void
      declined?: () => void
      error?: (errorMsg: string) => void
    }
  }) => void
}

declare global {
  interface Window {
    eversign?: EversignSDK
  }
}

/**
 * Run a callback while `document.getElementById` is temporarily patched to
 * also search the given `ShadowRoot`.
 *
 * This is useful for third-party scripts that hard-code
 * `document.getElementById(containerID)` and cannot see elements rendered
 * inside a shadow DOM. During `fn`, lookups first check the real document and
 * then fall back to `rootNode.getElementById(id)`. The original implementation
 * is always restored in a `finally` block.
 */
export function withShadowRootGetElementById<T>(
  rootNode: ShadowRoot,
  fn: () => T,
): T {
  const doc = document as Document & {
    getElementById(id: string): HTMLElement | null
  }

  const originalGetElementById = doc.getElementById.bind(doc)

  doc.getElementById = (id: string): HTMLElement | null => {
    const fromDocument = originalGetElementById(id)
    if (fromDocument) {
      return fromDocument
    }
    return rootNode.getElementById(id)
  }

  try {
    return fn()
  } finally {
    doc.getElementById = originalGetElementById
  }
}

let eversignPromise: Promise<EversignSDK> | null = null

function getEversignSdk(): Promise<EversignSDK> {
  if (window.eversign) {
    return Promise.resolve(window.eversign)
  }

  if (!eversignPromise) {
    eversignPromise = new Promise((resolve, reject) => {
      console.log("Loading Eversign SDK...")
      const script = document.createElement("script")
      script.src = "https://static.eversign.com/js/embedded-signing.js"
      script.onload = () => {
        if (window.eversign) {
          resolve(window.eversign)
        } else {
          reject(new Error("Eversign SDK not available"))
        }
      }
      script.onerror = () => reject(new Error("Failed to load Eversign SDK"))
      document.head.appendChild(script)
    })
  }

  return eversignPromise
}

type EversignContainerProps = {
  embeddedSigningUrl: string
  className?: string
  onLoaded?: () => void
  onSigned?: () => void
  onDeclined?: () => void
  onError?: (errorMsg: string) => void
}

export function EversignContainer({
  embeddedSigningUrl,
  className,
  onLoaded,
  onSigned,
  onDeclined,
  onError,
}: EversignContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [eversign, setEversign] = useState<EversignSDK | null>(null)

  useEffect(() => {
    getEversignSdk()
      .then((sdk) => {
        setEversign(sdk)
      })
      .catch((error) => {
        console.error("Failed to load Eversign SDK:", error)
      })
  }, [])

  useEffect(() => {
    if (!eversign || !containerRef.current) return
    console.log("Eversign SDK loaded, opening signing container...")

    const rootNode = containerRef.current.getRootNode()

    // Remove any existing iframes from the container
    containerRef.current.innerHTML = ""

    const config = {
      url: embeddedSigningUrl,
      containerID: containerRef.current.id,
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      events: {
        loaded: () => {
          onLoaded?.()
        },
        signed: () => {
          onSigned?.()
        },
        declined: () => {
          onDeclined?.()
        },
        error: (errorMsg: string) => {
          onError?.(errorMsg)
        },
      },
    }

    if (rootNode instanceof ShadowRoot) {
      withShadowRootGetElementById(rootNode, () => {
        eversign.open(config)
      })
    } else {
      eversign.open(config)
    }
  }, [eversign, embeddedSigningUrl])

  return (
    <div ref={containerRef} id="eversign-container" className={className} />
  )
}
